import {
  Observable,
  Subscription,
  EMPTY,
  of,
  mergeMap,
  distinctUntilChanged,
  map,
} from 'rxjs'
import { crc8 } from '@/ble/protocols/crc8'
import { parseFrame } from '@/ble/protocols/crc32AuthV14/crc32AuthParser'
import { V14TimeoutController } from '@/ble/protocols/crc32AuthV14/v14TimeoutController'
import { Crc32AuthOperationResultAdapter } from '@/ble/protocols/crc32AuthV14/crc32AuthOperationResultAdapter'
import { StandOperationResult } from '@/ble/entities/standOperationResult'
import { readAesResponse } from '@/ble/protocols/aes/aesPacket'
import { parseAesStandData } from '@/ble/protocols/aes/aesStandData'
import { AesOperationResultAdapter } from '@/ble/protocols/aes/aesOperationResultAdapter'

// V1.4: notify$ の各 emission を parseFrame に通し、ack/result を V14TimeoutController へ流す。
// 結果は controller.results$（ack3s/result15s の2段timeoutは controller 内で完結）。
// 破損frame（parseFrameがnullを返すもの）は破棄する（既存安全弁）。
export function wireV14ResultStream(
  notify$: Observable<Uint8Array>,
  controller: V14TimeoutController,
  adapter: Crc32AuthOperationResultAdapter
): { results$: Observable<StandOperationResult>; feedSub: Subscription } {
  const feedSub = notify$.subscribe((bytes) => {
    const event = parseFrame([...bytes])
    if (!event) return
    if (event.type === 'ack') {
      controller.onAckReceived()
    } else {
      controller.onResultReceived(adapter.convert(event.frame))
    }
  })
  return { results$: controller.results$, feedSub }
}

// 電池(0x08=GetBatteryStatus)応答のコマンドエコー（0x08|0x80）。readStandBattery が専用にこのエコーを
// 拾い、operation stream(wireAesResultStream)はこのエコーだけを除外する。
// ★operation の echo 値では demux しない: platform も AES は commandCode(decoded[0])を検証に使わず、
// 応答形式は canDistinguishReaders + length で決まる（echo 値で分岐しない）。世代により rental/return の
// echo は異なる（実機観測: 00_0=0x84 / 00_1・01=0x9d）ため、包含リスト方式は世代を取りこぼす。
export const AES_BATTERY_ECHO = 0x88

// フレームの健全性は暗号文長の構造だけで判定し、header/footer の"値"は検証しない。platform の
// handleResponse は枠バイトの値を見ず slice(6,-2) で暗号文を切り出すだけで、AES 応答の footer は
// 0xa5 に固定されない（実機で 0x5a を観測）。暗号文長=len-8 が正の16の倍数であることのみ要求する
// （AES-CBC 復号が16バイトブロック単位で成立する条件。platform も非16は decrypt が throw する）。
export function isWellFormedAesFrame(packet: Uint8Array): boolean {
  if (packet.length < 8) return false
  const cipherLen = packet.length - 8 // readAesResponse は slice(6, -2) を暗号文とする
  return cipherLen > 0 && cipherLen % 16 === 0
}

// AES 応答フレームのワイヤ crc8 検証（aikasa/ikasa_platform#7595）。crc8 は number[] 引数のため Array.from で変換。
// 不一致でも frame は捨てず warn ログのみに留める（warnIfAesCrcMismatch / wireAesResultStream）。
// 式（crc_8n init 0x79 / poly 0x8C・範囲は末尾2バイト手前まで）は旧AES/V1.4 の仕様書どおり正しく legacy 実機
// フレームでも一致するが、B系実機は自身の仕様から乖離しており（footer が実機で 0x5a になる前例と同型の
// 未文書化挙動）、drop するとその正常応答を誤って捨てるため warn 止まりとする。
export function aesFrameCrcOk(packet: Uint8Array): boolean {
  if (packet.length < 2) return false
  const body = Array.from(packet.slice(0, -2))
  return crc8(body) === packet[packet.length - 2]
}

// 観測のみ（frame は捨てない）。wireAesResultStream の1箇所から呼ぶ（demux より前なので結果・電池
// フレームの両方をここで観測する）。
export function warnIfAesCrcMismatch(packet: Uint8Array): void {
  if (!aesFrameCrcOk(packet)) {
    console.warn('[ble] AES frame crc mismatch (observing, not dropped)', {
      hex: Array.from(packet)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join(''),
    })
  }
}

function sameBytes(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i])
}

// AES: notify$ → 長さ検証 → 復号 → バッテリーecho(0x88)除外 → dedup → parse → convert。
// ★malformed は drop + onMalformed 通知だが stream を終端しない（preClosed→closed を守る）。
export function wireAesResultStream(
  notify$: Observable<Uint8Array>,
  canDistinguishReaders: boolean,
  adapter: AesOperationResultAdapter,
  onMalformed: (e: unknown) => void
): Observable<StandOperationResult> {
  return notify$.pipe(
    mergeMap((packet) => {
      try {
        if (!isWellFormedAesFrame(packet)) {
          onMalformed(new Error('malformed AES frame'))
          return EMPTY
        }
        warnIfAesCrcMismatch(packet) // crc 不一致を観測（frame は捨てない）
        const decoded = readAesResponse(packet)
        if (decoded[0] === AES_BATTERY_ECHO) return EMPTY // demux: バッテリー応答(0x88)のみ除外
        return of(decoded)
      } catch (e) {
        onMalformed(e) // 復号throwも drop + 通知・非終端
        return EMPTY
      }
    }),
    distinctUntilChanged(sameBytes),
    // NOTE(accept): この convert(map) は上の mergeMap try/catch の保護外だが、
    // parseAesStandData / adapter.convert は現状 total（throw 経路なし）のため live bug は無い。
    // もし将来 parse/convert を throw し得る形に変える場合は、AES 応答ストリームの「非終端」不変条件
    // （preClosed→closed の2段emitを守る）を保つため、この convert を distinctUntilChanged の後段の
    // 第2 mergeMap で try→onMalformed+EMPTY 保護すること（decode段と同じ drop・非終端パターン）。
    // ※subscribe への error handler 追加や第1 mergeMap への畳み込みは非終端契約/dedup を壊すため不可。
    map((decoded) =>
      adapter.convert(parseAesStandData(decoded, canDistinguishReaders))
    )
  )
}
